// frontend/src/components/cash-close/VarianceTableWideGrid.js
import React, { useState } from 'react';
import { useTheme, sans } from '../../tokens';
import VarianceCell from './VarianceCell';

const monthLabel = (ym) => {
  const parts = ym.split('-').map(Number);
  return new Date(parts[0], parts[1] - 1, 1).toLocaleDateString('en-US', { month: 'narrow' });
};

function Sparkline({ cells, width = 80, height = 18 }) {
  const values = cells.filter(c => c.actual != null).map(c => c.actual);
  if (values.length < 2) return <span />;
  const min = Math.min(...values), max = Math.max(...values);
  const span = max - min || 1;
  const stepX = width / (values.length - 1);
  const points = values.map((v, i) => `${i * stepX},${height - ((v - min) / span) * height}`).join(' ');
  return (
    <svg width={width} height={height} style={{ display: 'block' }}>
      <polyline points={points} fill="none" stroke="#888" strokeWidth={1} />
    </svg>
  );
}

export default function VarianceTableWideGrid({ rows, currency, fxRate, selectedMonth, drilldownRenderer, commentary, showNumbers, onToggleNumbers }) {
  const t = useTheme();
  const [openCell, setOpenCell] = useState(null);
  if (!rows.length) return null;
  const monthHeaders = rows[0].cells.map(c => c.month);

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 6 }}>
        <button onClick={onToggleNumbers}
                style={{ fontSize: 10, padding: '2px 8px', border: `1px solid ${t.border}`, borderRadius: 10,
                         background: showNumbers ? t.bgSurface : t.bgCard, color: t.textSecondary, cursor: 'pointer', fontFamily: sans }}>
          {showNumbers ? 'Hide numbers' : 'Show numbers'}
        </button>
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: sans, fontSize: 11 }}>
        <thead>
          <tr style={{ borderBottom: `1px solid ${t.border}` }}>
            <th style={{ textAlign: 'left', padding: '6px', fontSize: 10, fontWeight: 600, color: t.textMuted, textTransform: 'uppercase', letterSpacing: 0.5 }}>Department</th>
            {monthHeaders.map(m => (
              <th key={m} style={{ textAlign: 'center', padding: '4px 2px', fontSize: 9, color: m === selectedMonth ? t.textPrimary : t.textMuted }}>{monthLabel(m)}</th>
            ))}
            <th style={{ padding: '4px 8px', fontSize: 9, color: t.textMuted }}>Trend</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(({ dept, cells }) => (
            <tr key={dept} style={{ borderBottom: `1px solid ${t.borderLight}` }}>
              <td style={{ padding: '6px', color: t.textPrimary, whiteSpace: 'nowrap' }}>{dept}</td>
              {cells.map(c => (
                <td key={c.month} style={{ padding: 1 }}>
                  <VarianceCell
                    cell={c} currency={currency} fxRate={fxRate}
                    isSelected={c.month === selectedMonth}
                    compact={!showNumbers}
                    hasNote={!!commentary?.[`${dept}::${c.month}`]?.comment}
                    onClick={() => setOpenCell({ dept, month: c.month })}
                  />
                </td>
              ))}
              <td style={{ padding: '4px 8px' }}><Sparkline cells={cells} /></td>
            </tr>
          ))}
        </tbody>
      </table>
      {openCell && (
        <div style={{
          position: 'fixed', top: 0, right: 0, bottom: 0, width: '40vw',
          background: t.bgCard, borderLeft: `1px solid ${t.border}`,
          boxShadow: '-4px 0 16px rgba(0,0,0,0.06)', zIndex: 1000, overflowY: 'auto',
        }}>
          <div style={{ padding: '10px 14px', borderBottom: `1px solid ${t.borderLight}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ fontFamily: sans, fontWeight: 600 }}>{openCell.dept} — {openCell.month}</div>
            <button onClick={() => setOpenCell(null)} style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 16 }}>×</button>
          </div>
          <div>{drilldownRenderer(openCell.dept, openCell.month)}</div>
        </div>
      )}
    </>
  );
}
